<template>
  <Highcharts
    ref="chart"
    :options="chartOptions"
  />
</template>

<script>
import { Chart } from 'highcharts-vue';

import formatterTooltip from './formatter-tooltip.js';
import formattedLabelValue from './formatter-label-value';

export default {
  name: 'DualAxesLineAndColumn',
  components: {
    Highcharts: Chart,
  },
  props: {
    series: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    overrideChartOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    chartOptions() {
      return {
        credits: {
          enabled: false,
        },
        chart: {
          zoomType: 'xy',
        },
        title: {
          text: '',
        },
        xAxis: [
          {
            categories: this.categories,
            crosshair: {
              width: 1,
              color: 'var(--ds-color-gray-3)',
            },
          },
        ],
        legend: {
          align: 'left',
          margin: 18,
          padding: 0,
        },
        yAxis: [
          {
            // Primary yAxis
            title: {
              text: this.$t('Charts.interactionsAndEngagement.chartOptions.yAxis.rightVerticalTitle'),
              style: {
                color: 'var(--ds-color-text-2)',
                fontSize: 'var(--ds-font-size-xs)',
                fontWeight: 'var(--ds-font-weight-semibold)',
              },
            },
            labels: {
              formatter() {
                return formattedLabelValue(this.value);
              },
            },
            opposite: true,
          },
          {
            // Secondary yAxis
            title: {
              text: this.$t('Charts.interactionsAndEngagement.chartOptions.yAxis.leftVerticalTitle'),
              style: {
                color: 'var(--ds-color-text-2)',
                fontSize: 'var(--ds-font-size-xs)',
                fontWeight: 'var(--ds-font-weight-semibold)',
              },
            },
            labels: {
              formatter() {
                return formattedLabelValue(this.value);
              },
            },
          },
        ],
        tooltip: {
          shared: true,
          useHTML: true,
          shadow: false,
          borderRadius: 4,
          formatter() {
            return formatterTooltip(this);
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
            pointPadding: 0.01,
            marker: {
              enabled: false,
              states: {
                hover: {
                  lineColor: 'rgba(0,0,0,0.3)',
                  lineWidth: 4,
                },
              },
            },
            events: {
              legendItemClick: (e) => {
                this.$emit('legend-click', { legend: e.target.name });
              },
            },
          },
        },
        series: this.series,
        ...this.overrideChartOptions,
      };
    },
  },
};
</script>

<style scoped lang='scss'>
::v-deep {
  .highcharts {
    &-tooltip {
      border-radius: var(--ds-border-radius-sm);
    }
  }

  .highcharts-custom-tooltip {
    padding: var(--ds-spacing-inset-xs);
    box-shadow: var(--ds-light-dropshadow-1);
    border-radius: var(--ds-border-radius-sm);
    background: var(--ds-color-neutral);

    &__content {
      margin-top: var(--ds-spacing-stack-xs);
    }

    &__item {
      display: grid;
      grid-template-columns: 2fr 1fr;
      justify-content: space-between;
      gap: 36px;

      &:last-child {
        margin-top: var(--ds-spacing-stack-nano);
      }

      span:last-child {
        text-align: right;
      }

      .interactions {
        &__point {
          display: inline-block;
          border-radius: var(--ds-border-radius-pill);
          margin-right: var(--ds-spacing-stack-nano);
          padding: var(--ds-spacing-stack-quark);
        }
      }

      .engagement {
        display: flex;
        align-items: center;

        &::before {
          content: '';
          display: inline-block;
          width: 11px;
          height: 2px;
          margin-right: var(--ds-spacing-stack-nano);
          background: var(--ds-color-reverse);
        }
      }
    }

    &__name {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-xs);
      font-weight: var(--ds-font-weight-regular);
    }

    &__interations {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-xs);
      font-weight: var(--ds-font-weight-semibold);
    }

    &__engagement-rate {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-xs);
      font-weight: var(--ds-font-weight-semibold);
    }
  }
}
</style>
